import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Puzzle,
  Globe,
  Users,
  Slash,
  Search,
  Plus,
  Trash2,
  Check,
  ExternalLink,
  Shield,
  Layers,
  Sparkles,
  RefreshCw,
  Edit2,
  Info,
  X,
  Store,
} from 'lucide-react';
import ApiService from '../services/api';
import { resolvePluginIcon } from '../plugins/icons';
import { useToast } from './ToastProvider';

export default function AdminPluginsTab({ tenants: propTenants = [], refreshTick: _refreshTick = 0 }) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [plugins, setPlugins] = useState([]);
  const [tenants, setTenants] = useState(propTenants);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'builtin' | 'custom'
  const [savingId, setSavingId] = useState(null);

  // Edit / Add Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [modalPlugin, setModalPlugin] = useState(null);
  const [modalBusy, setModalBusy] = useState(false);

  // Load plugins and tenants
  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const res = await ApiService.getAdminPlugins();
      setPlugins(Array.isArray(res?.plugins) ? res.plugins : []);
      if (Array.isArray(res?.tenants) && res.tenants.length) {
        setTenants(res.tenants);
      }
    } catch (e) {
      setError(e?.message || 'Failed to load platform plugins');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Keep tenants in sync if parent passes them
  useEffect(() => {
    if (Array.isArray(propTenants) && propTenants.length) {
      setTenants(propTenants);
    }
  }, [propTenants]);

  // Handle quick availability change
  const handleAvailabilityChange = async (plugin, nextAvailability) => {
    const pluginId = plugin.id;
    const allowedTenants = plugin.allowedTenants || [];
    setSavingId(pluginId);
    try {
      const res = await ApiService.updateAdminPlugin(pluginId, {
        availability: nextAvailability,
        allowedTenants,
      });
      if (Array.isArray(res?.plugins)) {
        setPlugins(res.plugins);
      } else {
        setPlugins((prev) =>
          prev.map((p) =>
            p.id === pluginId
              ? { ...p, availability: nextAvailability }
              : p
          )
        );
      }
      showToast?.({
        type: 'success',
        title: 'Availability updated',
        message: `${plugin.name} is now ${nextAvailability === 'all' ? 'available to all tenants' : nextAvailability === 'specific' ? 'restricted to selected tenants' : 'globally disabled'}.`,
      });
    } catch (e) {
      showToast?.({
        type: 'error',
        title: 'Update failed',
        message: e?.message || 'Could not update plugin availability',
      });
    } finally {
      setSavingId(null);
    }
  };

  // Toggle tenant in allowed list
  const handleToggleTenant = async (plugin, tenantIdOrSlug) => {
    const pluginId = plugin.id;
    const currentList = Array.isArray(plugin.allowedTenants) ? [...plugin.allowedTenants] : [];
    const idx = currentList.findIndex((t) => t === tenantIdOrSlug);

    let nextList;
    if (idx >= 0) {
      nextList = currentList.filter((t) => t !== tenantIdOrSlug);
    } else {
      nextList = [...currentList, tenantIdOrSlug];
    }

    // Optimistic
    setPlugins((prev) =>
      prev.map((p) => (p.id === pluginId ? { ...p, allowedTenants: nextList } : p))
    );

    setSavingId(pluginId);
    try {
      const res = await ApiService.updateAdminPlugin(pluginId, {
        availability: 'specific',
        allowedTenants: nextList,
      });
      if (Array.isArray(res?.plugins)) {
        setPlugins(res.plugins);
      }
      showToast?.({
        type: 'success',
        title: 'Tenants updated',
        message: `Updated tenant access for ${plugin.name}.`,
      });
    } catch (e) {
      showToast?.({
        type: 'error',
        title: 'Update failed',
        message: e?.message || 'Could not update tenant access',
      });
    } finally {
      setSavingId(null);
    }
  };

  // Select all or clear tenants
  const handleBulkSetTenants = async (plugin, selectAll) => {
    const pluginId = plugin.id;
    const nextList = selectAll
      ? tenants.map((t) => String(t._id || t.id || t.slug)).filter(Boolean)
      : [];

    setSavingId(pluginId);
    try {
      const res = await ApiService.updateAdminPlugin(pluginId, {
        availability: 'specific',
        allowedTenants: nextList,
      });
      if (Array.isArray(res?.plugins)) {
        setPlugins(res.plugins);
      } else {
        setPlugins((prev) =>
          prev.map((p) => (p.id === pluginId ? { ...p, allowedTenants: nextList } : p))
        );
      }
      showToast?.({
        type: 'success',
        title: selectAll ? 'All tenants selected' : 'Tenants cleared',
        message: selectAll ? `All ${tenants.length} tenants granted access to ${plugin.name}.` : `Removed tenant access list for ${plugin.name}.`,
      });
    } catch (e) {
      showToast?.({
        type: 'error',
        title: 'Update failed',
        message: e?.message || 'Failed bulk updating tenants',
      });
    } finally {
      setSavingId(null);
    }
  };

  // Delete custom plugin
  const handleDeleteCustomPlugin = async (plugin) => {
    if (!plugin || plugin.type !== 'custom') return;
    if (!window.confirm(`Are you sure you want to delete custom plugin "${plugin.name}" (${plugin.id})? This cannot be undone.`)) return;

    setSavingId(plugin.id);
    try {
      const res = await ApiService.deleteAdminPlugin(plugin.id);
      if (Array.isArray(res?.plugins)) {
        setPlugins(res.plugins);
      } else {
        setPlugins((prev) => prev.filter((p) => p.id !== plugin.id));
      }
      showToast?.({
        type: 'success',
        title: 'Plugin deleted',
        message: `Custom plugin "${plugin.name}" removed from platform.`,
      });
    } catch (e) {
      showToast?.({
        type: 'error',
        title: 'Delete failed',
        message: e?.message || 'Could not delete plugin',
      });
    } finally {
      setSavingId(null);
    }
  };

  // Save Modal (Create / Edit custom plugin)
  const handleSaveModal = async (e) => {
    e?.preventDefault?.();
    if (!modalPlugin?.id || !modalPlugin?.name) {
      showToast?.({ type: 'warning', title: 'Validation error', message: 'ID and Name are required' });
      return;
    }

    setModalBusy(true);
    try {
      const res = await ApiService.createAdminPlugin(modalPlugin);
      if (Array.isArray(res?.plugins)) {
        setPlugins(res.plugins);
      } else {
        await load();
      }
      setShowModal(false);
      showToast?.({
        type: 'success',
        title: modalMode === 'edit' ? 'Plugin updated' : 'Custom plugin registered',
        message: `Plugin "${modalPlugin.name}" is now configured.`,
      });
    } catch (e) {
      showToast?.({
        type: 'error',
        title: 'Save failed',
        message: e?.message || 'Could not save plugin',
      });
    } finally {
      setModalBusy(false);
    }
  };

  // Filtered plugins
  const filteredPlugins = useMemo(() => {
    return plugins.filter((p) => {
      if (typeFilter === 'builtin' && p.type !== 'builtin') return false;
      if (typeFilter === 'custom' && p.type !== 'custom') return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchName = String(p.name || '').toLowerCase().includes(q);
        const matchId = String(p.id || '').toLowerCase().includes(q);
        const matchTagline = String(p.tagline || '').toLowerCase().includes(q);
        const matchSummary = String(p.summary || '').toLowerCase().includes(q);
        if (!matchName && !matchId && !matchTagline && !matchSummary) return false;
      }
      return true;
    });
  }, [plugins, typeFilter, searchQuery]);

  // Statistics
  const stats = useMemo(() => {
    const total = plugins.length;
    const builtinCount = plugins.filter((p) => p.type === 'builtin').length;
    const customCount = plugins.filter((p) => p.type === 'custom').length;
    const allTenantsCount = plugins.filter((p) => p.availability === 'all').length;
    const specificTenantsCount = plugins.filter((p) => p.availability === 'specific').length;
    const disabledCount = plugins.filter((p) => p.availability === 'disabled').length;
    return { total, builtinCount, customCount, allTenantsCount, specificTenantsCount, disabledCount };
  }, [plugins]);

  return (
    <div className="space-y-6 max-w-6xl pb-16">
      {/* Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/10 pb-5">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-teal-500/15 text-teal-300 border border-teal-500/30">
              <Store className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-100">Marketplace & Apps Distribution</h2>
              <p className="text-xs text-slate-400 mt-0.5">
                Manage built-in and custom marketplace apps. Control whether each app is available to all tenants, restricted to specific workspaces, or disabled.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-white/10 bg-surface-1 text-slate-300 hover:text-white hover:bg-white/5 transition disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Reload
          </button>
          <button
            type="button"
            onClick={() => {
              setModalMode('create');
              setModalPlugin({
                id: '',
                name: '',
                tagline: '',
                kind: 'link',
                type: 'custom',
                badge: 'Custom',
                icon: 'Puzzle',
                url: 'https://',
                summary: '',
                description: '',
                availability: 'all',
                allowedTenants: [],
              });
              setShowModal(true);
            }}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-teal-500 hover:bg-teal-400 text-slate-950 shadow-sm transition"
          >
            <Plus className="w-3.5 h-3.5" />
            Register Custom App
          </button>
        </div>
      </div>

      {/* KPI Stats Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="p-3 rounded-xl border border-white/10 bg-white/[0.03]">
          <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-400">Total Apps</div>
          <div className="text-xl font-bold text-slate-100 mt-1 tabular-nums">{stats.total}</div>
        </div>
        <div className="p-3 rounded-xl border border-teal-500/20 bg-teal-500/[0.04]">
          <div className="text-[10px] uppercase font-semibold tracking-wider text-teal-400">Built-in</div>
          <div className="text-xl font-bold text-teal-200 mt-1 tabular-nums">{stats.builtinCount}</div>
        </div>
        <div className="p-3 rounded-xl border border-violet-500/20 bg-violet-500/[0.04]">
          <div className="text-[10px] uppercase font-semibold tracking-wider text-violet-400">Custom</div>
          <div className="text-xl font-bold text-violet-200 mt-1 tabular-nums">{stats.customCount}</div>
        </div>
        <div className="p-3 rounded-xl border border-sky-500/20 bg-sky-500/[0.04]">
          <div className="text-[10px] uppercase font-semibold tracking-wider text-sky-400">All Tenants</div>
          <div className="text-xl font-bold text-sky-200 mt-1 tabular-nums">{stats.allTenantsCount}</div>
        </div>
        <div className="p-3 rounded-xl border border-amber-500/20 bg-amber-500/[0.04]">
          <div className="text-[10px] uppercase font-semibold tracking-wider text-amber-400">Specific Tenants</div>
          <div className="text-xl font-bold text-amber-200 mt-1 tabular-nums">{stats.specificTenantsCount}</div>
        </div>
        <div className="p-3 rounded-xl border border-rose-500/20 bg-rose-500/[0.04]">
          <div className="text-[10px] uppercase font-semibold tracking-wider text-rose-400">Disabled</div>
          <div className="text-xl font-bold text-rose-200 mt-1 tabular-nums">{stats.disabledCount}</div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 p-1 bg-surface-1 border border-white/10 rounded-xl">
          <button
            type="button"
            onClick={() => setTypeFilter('all')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
              typeFilter === 'all'
                ? 'bg-teal-500/20 text-teal-200 border border-teal-500/40'
                : 'text-slate-400 hover:text-slate-200 border border-transparent'
            }`}
          >
            All ({plugins.length})
          </button>
          <button
            type="button"
            onClick={() => setTypeFilter('builtin')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
              typeFilter === 'builtin'
                ? 'bg-teal-500/20 text-teal-200 border border-teal-500/40'
                : 'text-slate-400 hover:text-slate-200 border border-transparent'
            }`}
          >
            Built-in ({stats.builtinCount})
          </button>
          <button
            type="button"
            onClick={() => setTypeFilter('custom')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
              typeFilter === 'custom'
                ? 'bg-violet-500/20 text-violet-200 border border-violet-500/40'
                : 'text-slate-400 hover:text-slate-200 border border-transparent'
            }`}
          >
            Custom ({stats.customCount})
          </button>
        </div>

        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search marketplace apps by name, ID or tagline..."
            className="w-full bg-surface-1 border border-white/10 rounded-xl pl-9 pr-3 py-1.5 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-teal-500/50"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {error && <div className="text-xs text-rose-300 bg-rose-950/40 border border-rose-500/40 p-3 rounded-xl">{error}</div>}

      {/* Apps Grid / List */}
      <div className="space-y-4">
        {filteredPlugins.map((plugin) => (
          <PluginAdminRow
            key={plugin.id}
            plugin={plugin}
            tenants={tenants}
            isSaving={savingId === plugin.id}
            onAvailabilityChange={(next) => handleAvailabilityChange(plugin, next)}
            onToggleTenant={(tId) => handleToggleTenant(plugin, tId)}
            onBulkSetTenants={(selAll) => handleBulkSetTenants(plugin, selAll)}
            onEdit={() => {
              setModalMode('edit');
              setModalPlugin({ ...plugin });
              setShowModal(true);
            }}
            onDelete={() => handleDeleteCustomPlugin(plugin)}
          />
        ))}

        {!loading && filteredPlugins.length === 0 && (
          <div className="p-8 text-center border border-white/10 rounded-2xl bg-white/[0.02]">
            <Store className="w-8 h-8 mx-auto text-slate-600 mb-2" />
            <div className="text-sm font-semibold text-slate-300">No apps match your filter</div>
            <p className="text-xs text-slate-500 mt-1">Try adjusting the search query or category filter.</p>
          </div>
        )}
      </div>

      {/* Modal for Add / Edit Custom App */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm animate-[fadeIn_0.15s_ease-out]">
          <div className="relative w-full max-w-2xl bg-surface-1 border border-white/15 rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-white/[0.02]">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-teal-400" />
                <h3 className="text-base font-semibold text-slate-100">
                  {modalMode === 'edit' ? `Edit Custom App: ${modalPlugin?.name}` : 'Register New Custom App'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-white/10 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveModal} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">
                    App ID <span className="text-rose-400">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    disabled={modalMode === 'edit'}
                    value={modalPlugin?.id || ''}
                    onChange={(e) => setModalPlugin((p) => ({ ...p, id: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') }))}
                    placeholder="e.g. status-page, custom-ci"
                    className="w-full bg-surface-2 border border-white/10 rounded-xl px-3 py-2 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-teal-500 disabled:opacity-50 font-mono"
                  />
                  <span className="text-[10px] text-slate-500 mt-0.5 block">Unique slug for routes & storage</span>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">
                    Display Name <span className="text-rose-400">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={modalPlugin?.name || ''}
                    onChange={(e) => setModalPlugin((p) => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. Production Status, Jenkins Bridge"
                    className="w-full bg-surface-2 border border-white/10 rounded-xl px-3 py-2 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-teal-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">Kind</label>
                  <select
                    value={modalPlugin?.kind || 'link'}
                    onChange={(e) => setModalPlugin((p) => ({ ...p, kind: e.target.value }))}
                    className="w-full bg-surface-2 border border-white/10 rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-teal-500"
                  >
                    <option value="link">Link (Opens in new tab)</option>
                    <option value="sso">SSO (Handoff token to companion app)</option>
                    <option value="route">Route (In-app navigation tab)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">Badge Label</label>
                  <input
                    type="text"
                    value={modalPlugin?.badge || 'Custom'}
                    onChange={(e) => setModalPlugin((p) => ({ ...p, badge: e.target.value }))}
                    placeholder="Custom, Live, Companion"
                    className="w-full bg-surface-2 border border-white/10 rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">Icon (Lucide name)</label>
                  <input
                    type="text"
                    value={modalPlugin?.icon || 'Puzzle'}
                    onChange={(e) => setModalPlugin((p) => ({ ...p, icon: e.target.value }))}
                    placeholder="Puzzle, Crosshair, Globe, Shield"
                    className="w-full bg-surface-2 border border-white/10 rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-teal-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">Tagline</label>
                <input
                  type="text"
                  value={modalPlugin?.tagline || ''}
                  onChange={(e) => setModalPlugin((p) => ({ ...p, tagline: e.target.value }))}
                  placeholder="Short one-line pitch"
                  className="w-full bg-surface-2 border border-white/10 rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-teal-500"
                />
              </div>

              {modalPlugin?.kind === 'link' && (
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">Destination URL</label>
                  <input
                    type="url"
                    value={modalPlugin?.open?.url || modalPlugin?.url || ''}
                    onChange={(e) => {
                      const url = e.target.value;
                      setModalPlugin((p) => ({ ...p, url, open: { ...(p.open || {}), url } }));
                    }}
                    placeholder="https://status.example.com"
                    className="w-full bg-surface-2 border border-white/10 rounded-xl px-3 py-2 text-xs text-slate-200 font-mono focus:outline-none focus:border-teal-500"
                  />
                </div>
              )}

              {modalPlugin?.kind === 'sso' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1">SSO API Path</label>
                    <input
                      type="text"
                      value={modalPlugin?.open?.ssoPath || ''}
                      onChange={(e) => setModalPlugin((p) => ({ ...p, open: { ...(p.open || {}), ssoPath: e.target.value } }))}
                      placeholder="/api/probe/sso"
                      className="w-full bg-surface-2 border border-white/10 rounded-xl px-3 py-2 text-xs text-slate-200 font-mono focus:outline-none focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1">Fallback URL</label>
                    <input
                      type="url"
                      value={modalPlugin?.open?.fallbackUrl || ''}
                      onChange={(e) => setModalPlugin((p) => ({ ...p, open: { ...(p.open || {}), fallbackUrl: e.target.value } }))}
                      placeholder="https://probe.testera.io"
                      className="w-full bg-surface-2 border border-white/10 rounded-xl px-3 py-2 text-xs text-slate-200 font-mono focus:outline-none focus:border-teal-500"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">Summary</label>
                <textarea
                  rows={2}
                  value={modalPlugin?.summary || ''}
                  onChange={(e) => setModalPlugin((p) => ({ ...p, summary: e.target.value }))}
                  placeholder="Summary for catalog card view..."
                  className="w-full bg-surface-2 border border-white/10 rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-teal-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">Detailed Description</label>
                <textarea
                  rows={3}
                  value={modalPlugin?.description || ''}
                  onChange={(e) => setModalPlugin((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Full description for detail pane..."
                  className="w-full bg-surface-2 border border-white/10 rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-teal-500"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-medium text-slate-300 hover:bg-white/10 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={modalBusy}
                  className="px-5 py-2 rounded-xl text-xs font-semibold bg-teal-500 hover:bg-teal-400 text-slate-950 transition disabled:opacity-50"
                >
                  {modalBusy ? 'Saving…' : modalMode === 'edit' ? 'Save Changes' : 'Register App'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Single plugin row with tenant availability selector and interactive whitelist.
 */
function PluginAdminRow({
  plugin,
  tenants = [],
  isSaving = false,
  onAvailabilityChange,
  onToggleTenant,
  onBulkSetTenants,
  onEdit,
  onDelete,
}) {
  const [showTenantPicker, setShowTenantPicker] = useState(false);
  const [tenantSearch, setTenantSearch] = useState('');

  const IconComponent = resolvePluginIcon(plugin.icon);
  const isBuiltin = plugin.type === 'builtin';
  const availability = plugin.availability || 'all';
  const allowedTenants = Array.isArray(plugin.allowedTenants) ? plugin.allowedTenants : [];

  const filteredTenants = useMemo(() => {
    if (!tenantSearch.trim()) return tenants;
    const q = tenantSearch.toLowerCase();
    return tenants.filter(
      (t) =>
        String(t.name || '').toLowerCase().includes(q) ||
        String(t.slug || '').toLowerCase().includes(q)
    );
  }, [tenants, tenantSearch]);

  const enabledPercent = plugin.totalEligibleTenants > 0
    ? Math.round(((plugin.enabledCount || 0) / plugin.totalEligibleTenants) * 100)
    : 0;

  return (
    <div className="app-panel border border-white/10 rounded-2xl p-5 space-y-4 bg-white/[0.02]">
      {/* Top row: Icon, Name, Type, Kind, Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-surface-2 border border-white/10 flex items-center justify-center shrink-0 overflow-hidden">
            {plugin.iconUrl ? (
              <img src={plugin.iconUrl} alt={plugin.name} className="w-8 h-8 object-contain" />
            ) : IconComponent ? (
              <IconComponent className="w-6 h-6 text-teal-400" />
            ) : (
              <Puzzle className="w-6 h-6 text-slate-400" />
            )}
          </div>

          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-100 text-sm">{plugin.name}</span>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-white/10 text-slate-400">
                {plugin.id}
              </span>
              <span
                className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                  isBuiltin
                    ? 'border-teal-500/30 bg-teal-500/10 text-teal-300'
                    : 'border-violet-500/30 bg-violet-500/10 text-violet-300'
                }`}
              >
                {isBuiltin ? 'Built-in' : 'Custom'}
              </span>
              <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded bg-surface-2 text-slate-400 border border-white/5">
                {plugin.kind || 'route'}
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-0.5">{plugin.tagline || plugin.summary}</p>
          </div>
        </div>

        {/* Action buttons (Edit/Delete for custom) */}
        {!isBuiltin && (
          <div className="flex items-center gap-2 self-start sm:self-center shrink-0">
            <button
              type="button"
              onClick={onEdit}
              className="p-1.5 rounded-lg border border-white/10 bg-surface-1 text-slate-300 hover:text-white hover:bg-white/5 transition text-xs flex items-center gap-1"
              title="Edit manifest"
            >
              <Edit2 className="w-3.5 h-3.5" />
              <span>Edit</span>
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="p-1.5 rounded-lg border border-rose-500/30 bg-rose-950/20 text-rose-300 hover:bg-rose-950/40 hover:border-rose-500/50 transition text-xs flex items-center gap-1"
              title="Delete custom plugin"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* Middle row: Availability Segmented Control */}
      <div className="p-3.5 rounded-xl bg-surface-1 border border-white/5 flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-300">Tenant Availability:</span>
          {isSaving && <span className="text-[11px] text-teal-400 animate-pulse">Saving…</span>}
        </div>

        <div className="flex items-center gap-1.5 p-1 bg-surface-2 border border-white/10 rounded-xl self-start md:self-auto">
          <button
            type="button"
            disabled={isSaving}
            onClick={() => onAvailabilityChange('all')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              availability === 'all'
                ? 'bg-sky-500 text-slate-950 shadow'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <Globe className="w-3.5 h-3.5" />
            All Tenants
          </button>
          <button
            type="button"
            disabled={isSaving}
            onClick={() => {
              onAvailabilityChange('specific');
              setShowTenantPicker(true);
            }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              availability === 'specific'
                ? 'bg-amber-500 text-slate-950 shadow'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <Users className="w-3.5 h-3.5" />
            Specific Tenants {allowedTenants.length > 0 ? `(${allowedTenants.length})` : ''}
          </button>
          <button
            type="button"
            disabled={isSaving}
            onClick={() => onAvailabilityChange('disabled')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              availability === 'disabled'
                ? 'bg-rose-500 text-white shadow'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <Slash className="w-3.5 h-3.5" />
            Disabled
          </button>
        </div>
      </div>

      {/* Tenant Whitelist Selection Drawer (When 'specific' is active) */}
      {availability === 'specific' && (
        <div className="p-4 rounded-xl border border-amber-500/20 bg-amber-500/[0.02] space-y-3 animate-[fadeIn_0.15s_ease-out]">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-white/5 pb-2">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-amber-400" />
              <span className="text-xs font-semibold text-slate-200">
                Whitelisted Tenants ({allowedTenants.length} of {tenants.length} selected)
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onBulkSetTenants(true)}
                className="text-[11px] font-medium text-amber-400 hover:text-amber-300 underline underline-offset-2"
              >
                Select All
              </button>
              <span className="text-slate-600">·</span>
              <button
                type="button"
                onClick={() => onBulkSetTenants(false)}
                className="text-[11px] font-medium text-slate-400 hover:text-slate-200 underline underline-offset-2"
              >
                Clear All
              </button>
            </div>
          </div>

          {/* Search box for tenants */}
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={tenantSearch}
              onChange={(e) => setTenantSearch(e.target.value)}
              placeholder="Search tenants by name or slug..."
              className="w-full bg-surface-1 border border-white/10 rounded-lg pl-8 pr-3 py-1 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-amber-500/50"
            />
          </div>

          {/* Tenants Checkbox Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto pr-1">
            {filteredTenants.map((t) => {
              const tId = String(t._id || t.id);
              const tSlug = String(t.slug || '');
              const isSelected = allowedTenants.includes(tId) || allowedTenants.includes(tSlug);

              return (
                <button
                  key={tId || tSlug}
                  type="button"
                  onClick={() => onToggleTenant(tId || tSlug)}
                  className={`flex items-center justify-between gap-2 p-2 rounded-lg border text-left transition ${
                    isSelected
                      ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
                      : 'border-white/5 bg-surface-1 text-slate-400 hover:border-white/15 hover:text-slate-200'
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium truncate text-slate-200">{t.name || t.slug}</div>
                    <div className="text-[10px] text-slate-500 font-mono truncate">{t.slug} · {t.plan || 'free'}</div>
                  </div>
                  <div
                    className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                      isSelected ? 'bg-amber-500 border-amber-500 text-slate-950' : 'border-slate-600 bg-surface-2'
                    }`}
                  >
                    {isSelected && <Check className="w-3 h-3 stroke-[3]" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Bottom row: Adoption summary badge */}
      <div className="flex items-center justify-between text-xs text-slate-400 pt-1 border-t border-white/5">
        <div className="flex items-center gap-1.5">
          <Info className="w-3.5 h-3.5 text-slate-500" />
          <span>
            {availability === 'all'
              ? 'Available to all workspaces'
              : availability === 'specific'
              ? `Restricted to ${allowedTenants.length} workspaces`
              : 'Disabled globally'}
          </span>
        </div>

        <div className="text-[11px] font-mono text-slate-400">
          Enabled in <span className="text-slate-200 font-semibold">{plugin.enabledCount || 0}</span> of{' '}
          <span className="text-slate-200 font-semibold">{plugin.totalEligibleTenants || 0}</span> eligible workspaces ({enabledPercent}%)
        </div>
      </div>
    </div>
  );
}
